// ----------------------------------------------------------------------
// Cache Utility with TTL support and nested data handling
// ----------------------------------------------------------------------

const CACHE_PREFIX = 'api_cache_';
const DEFAULT_TTL = 10 * 60 * 1000; // 10 minutes in milliseconds

// Cache entry structure: { data, timestamp, ttl }
class CacheManager {
  constructor() {
    this.memoryCache = new Map(); // In-memory cache for faster access
    this.storageAvailable = CacheManager.checkStorageAvailable();
  }

  static checkStorageAvailable() {
    try {
      const testKey = '__cache_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }

  // Generate cache key from URL and params
  // eslint-disable-next-line class-methods-use-this
  generateCacheKey(url, params = {}) {
    // Normalize URL - remove protocol and domain if present
    let normalizedUrl = url;
    if (url.includes('://')) {
      try {
        const urlObj = new URL(url);
        normalizedUrl = urlObj.pathname + urlObj.search;
      } catch {
        // If URL parsing fails, use as-is
      }
    }

    const sortedParams = Object.keys(params)
      .sort()
      .map((key) => `${key}=${JSON.stringify(params[key])}`)
      .join('&');
    const paramString = sortedParams ? `?${sortedParams}` : '';
    return `${CACHE_PREFIX}${normalizedUrl}${paramString}`;
  }

  // Generate cache key for nested resources (e.g., package with treatment)
  static generateNestedCacheKey(baseKey, nestedResource, nestedId) {
    return `${baseKey}::nested::${nestedResource}::${nestedId}`;
  }

  // Get cache entry
  get(key) {
    // Check memory cache first
    if (this.memoryCache.has(key)) {
      const entry = this.memoryCache.get(key);
      if (CacheManager.isValid(entry)) {
        return entry.data;
      }
      // Expired, remove from memory
      this.memoryCache.delete(key);
    }

    // Check localStorage
    if (this.storageAvailable) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const entry = JSON.parse(stored);
          if (CacheManager.isValid(entry)) {
            // Restore to memory cache
            this.memoryCache.set(key, entry);
            return entry.data;
          }
          // Expired, remove from storage
          localStorage.removeItem(key);
        }
      } catch (error) {
        console.error('Cache get error:', error);
      }
    }

    return null;
  }

  // Set cache entry
  set(key, data, ttl = DEFAULT_TTL) {
    const entry = {
      data: CacheManager.deepClone(data), // Deep clone to prevent reference issues
      timestamp: Date.now(),
      ttl,
    };

    // Store in memory cache
    this.memoryCache.set(key, entry);

    // Store in localStorage
    if (this.storageAvailable) {
      try {
        localStorage.setItem(key, JSON.stringify(entry));
      } catch (error) {
        // If storage is full, clear old entries
        if (error.name === 'QuotaExceededError') {
          this.clearExpired();
          try {
            localStorage.setItem(key, JSON.stringify(entry));
          } catch (retryError) {
            console.warn('Cache storage full, entry not persisted:', retryError);
          }
        } else {
          console.error('Cache set error:', error);
        }
      }
    }
  }

  // Check if cache entry is valid (not expired)
  static isValid(entry) {
    if (!entry || !entry.timestamp) return false;
    const age = Date.now() - entry.timestamp;
    return age < entry.ttl;
  }

  // Invalidate cache by key pattern
  invalidate(pattern) {
    const keysToRemove = [];
    const normalizedPattern = pattern.startsWith(CACHE_PREFIX) ? pattern : `${CACHE_PREFIX}${pattern}`;

    // Check memory cache
    Array.from(this.memoryCache.keys()).forEach((key) => {
      if (CacheManager.matchesPattern(key, normalizedPattern)) {
        keysToRemove.push(key);
      }
    });

    // Check localStorage
    if (this.storageAvailable) {
      try {
        const storageKeys = [];
        let i = 0;
        while (i < localStorage.length) {
          const key = localStorage.key(i);
          if (key) {
            storageKeys.push(key);
          }
          i += 1;
        }
        storageKeys.forEach((key) => {
          if (key.startsWith(CACHE_PREFIX) && CacheManager.matchesPattern(key, normalizedPattern)) {
            keysToRemove.push(key);
          }
        });
      } catch (error) {
        console.error('Cache invalidation error:', error);
      }
    }

    // Remove all matching keys
    keysToRemove.forEach((key) => {
      this.memoryCache.delete(key);
      if (this.storageAvailable) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing cache key:', error);
        }
      }
    });

    return keysToRemove.length;
  }

  // Invalidate all cache entries for a resource type
  invalidateResource(resourceType, resourceId = null) {
    const patterns = [];

    // Base pattern for the resource
    patterns.push(`/${resourceType}`);
    patterns.push(`/${resourceType}/`);

    if (resourceId) {
      // Specific resource
      patterns.push(`/${resourceType}/${resourceId}`);
      patterns.push(`/${resourceType}/${resourceId}/`);
    }

    // Also invalidate list endpoints
    patterns.push(`/${resourceType}?`);

    // Invalidate all matching patterns
    patterns.forEach((pattern) => this.invalidate(pattern));

    // Invalidate nested cache entries
    if (resourceId) {
      this.invalidateNested(resourceType, resourceId);
    }
  }

  // Invalidate nested cache entries
  invalidateNested(resourceType, resourceId) {
    const nestedPattern = `::nested::${resourceType}::${resourceId}`;
    this.invalidate(nestedPattern);
  }

  // Match key against pattern (supports wildcards and partial matches)
  static matchesPattern(key, pattern) {
    if (typeof pattern === 'string') {
      // Exact match
      if (key === pattern) return true;
      // Starts with pattern
      if (key.startsWith(pattern)) return true;
      // Contains pattern (for nested resources)
      if (key.includes(pattern)) return true;
      // Regex pattern
      try {
        const regex = new RegExp(pattern);
        return regex.test(key);
      } catch {
        // Not a valid regex, ignore
      }
    } else if (pattern instanceof RegExp) {
      return pattern.test(key);
    }
    return false;
  }

  // Clear all expired entries
  clearExpired() {
    const keysToRemove = [];

    // Check memory cache
    Array.from(this.memoryCache.entries()).forEach(([key, entry]) => {
      if (!CacheManager.isValid(entry)) {
        keysToRemove.push(key);
      }
    });

    // Check localStorage
    if (this.storageAvailable) {
      try {
        const storageKeys = [];
        let i = 0;
        while (i < localStorage.length) {
          const key = localStorage.key(i);
          if (key) {
            storageKeys.push(key);
          }
          i += 1;
        }
        storageKeys.forEach((key) => {
          if (key && key.startsWith(CACHE_PREFIX)) {
            try {
              const stored = localStorage.getItem(key);
              if (stored) {
                const entry = JSON.parse(stored);
                if (!CacheManager.isValid(entry)) {
                  keysToRemove.push(key);
                }
              }
            } catch {
              // Invalid entry, remove it
              keysToRemove.push(key);
            }
          }
        });
      } catch (error) {
        console.error('Error clearing expired cache:', error);
      }
    }

    // Remove expired keys
    keysToRemove.forEach((key) => {
      this.memoryCache.delete(key);
      if (this.storageAvailable) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error removing expired cache key:', error);
        }
      }
    });

    return keysToRemove.length;
  }

  // Clear all cache
  clear() {
    this.memoryCache.clear();

    if (this.storageAvailable) {
      try {
        const keysToRemove = [];
        let i = 0;
        while (i < localStorage.length) {
          const key = localStorage.key(i);
          if (key && key.startsWith(CACHE_PREFIX)) {
            keysToRemove.push(key);
          }
          i += 1;
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      } catch (error) {
        console.error('Error clearing cache:', error);
      }
    }
  }

  // Deep clone utility
  static deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map((item) => CacheManager.deepClone(item));
    if (typeof obj === 'object') {
      const cloned = {};
      Object.keys(obj).forEach((key) => {
        cloned[key] = CacheManager.deepClone(obj[key]);
      });
      return cloned;
    }
    return obj;
  }

  // Get cache statistics
  getStats() {
    const memoryCount = this.memoryCache.size;
    let storageCount = 0;

    if (this.storageAvailable) {
      try {
        let i = 0;
        while (i < localStorage.length) {
          const key = localStorage.key(i);
          if (key && key.startsWith(CACHE_PREFIX)) {
            storageCount += 1;
          }
          i += 1;
        }
      } catch (error) {
        console.error('Error getting cache stats:', error);
      }
    }

    return {
      memoryEntries: memoryCount,
      storageEntries: storageCount,
      totalEntries: Math.max(memoryCount, storageCount),
    };
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

// Auto-cleanup expired entries every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    cacheManager.clearExpired();
  }, 5 * 60 * 1000);
}

export default cacheManager;
export { CacheManager };

// ----------------------------------------------------------------------
// Helper functions for common cache operations
// ----------------------------------------------------------------------

// Extract resource type and ID from URL
export const extractResourceInfo = (url) => {
  // Remove query string for matching
  const urlWithoutQuery = url.split('?')[0];

  // Match /v1/resource/id pattern
  const match = urlWithoutQuery.match(/\/v1\/([^/]+)\/([^/]+)$/);
  if (match) {
    return {
      resourceType: match[1],
      resourceId: match[2],
    };
  }

  // Match /v1/resource/ pattern (with trailing slash or end)
  const listMatch = urlWithoutQuery.match(/\/v1\/([^/]+)(?:\/|$)/);
  if (listMatch) {
    return {
      resourceType: listMatch[1],
      resourceId: null,
    };
  }

  // Try without /v1 prefix
  const simpleMatch = urlWithoutQuery.match(/\/([^/]+)\/([^/]+)$/);
  if (simpleMatch) {
    return {
      resourceType: simpleMatch[1],
      resourceId: simpleMatch[2],
    };
  }

  const simpleListMatch = urlWithoutQuery.match(/\/([^/]+)(?:\/|$)/);
  if (simpleListMatch) {
    return {
      resourceType: simpleListMatch[1],
      resourceId: null,
    };
  }

  return { resourceType: null, resourceId: null };
};

// Determine if a URL should be cached
export const shouldCache = (method, url) => {
  // Only cache GET requests
  if (method.toLowerCase() !== 'get') return false;

  // Don't cache auth endpoints
  if (url.includes('/auth/')) return false;

  // Don't cache search endpoints (they're dynamic)
  if (url.includes('/search')) return false;
  if (url.includes('/users-telecare-package-responses/list')) return false;
  if (url.includes('/users-telecare-package-responses')) return false;
  if (url.includes('/v1/pharmacy-orders/list')) return false;
  if (url.includes('/images/private')) return false;
  if (url.includes('/image/private')) return false;

  return true;
};

// Determine cache invalidation patterns for mutations
export const getInvalidationPatterns = (method, url) => {
  const patterns = [];
  const { resourceType, resourceId } = extractResourceInfo(url);

  if (resourceType) {
    // Invalidate list endpoints (with and without /v1 prefix)
    patterns.push(`/v1/${resourceType}`);
    patterns.push(`/v1/${resourceType}?`);
    patterns.push(`/${resourceType}`);
    patterns.push(`/${resourceType}?`);

    if (resourceId) {
      // Invalidate specific resource
      patterns.push(`/v1/${resourceType}/${resourceId}`);
      patterns.push(`/v1/${resourceType}/${resourceId}/`);
      patterns.push(`/${resourceType}/${resourceId}`);
      patterns.push(`/${resourceType}/${resourceId}/`);

      // For nested resources, invalidate parent resources
      // e.g., updating a package should invalidate clinic dashboard
      if (resourceType === 'packages') {
        patterns.push('/v1/clinics/dashboard');
        patterns.push('/clinics/dashboard');
      }
      if (resourceType === 'treatments' || resourceType === 'concerns') {
        // Invalidate packages that might reference this treatment/concern
        patterns.push('/v1/packages');
        patterns.push('/packages');
      }
      if (resourceType === 'clinics') {
        // Invalidate clinic dashboard when clinic is updated
        patterns.push('/v1/clinics/dashboard');
        patterns.push('/clinics/dashboard');
      }
    } else if (resourceType === 'packages') {
      // Creating a new resource - invalidate lists
      patterns.push('/v1/clinics/dashboard');
      patterns.push('/clinics/dashboard');
    }
  }

  return patterns;
};

